// frontend/src/pages/ClientHealthPage.tsx
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, PackageX, PackageCheck, CheckCircle2, Clock, Users, Ban } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DashboardLayout } from '@/components/DashboardLayout';
import { AssignPlanModal } from '@/components/clienthealth/AssignPlanModal';
import { api } from '@/lib/api';
import type { ClientHealthRow, DeliveryHealth, SubscriptionFlag } from '@/types/clientHealth';

const healthBadge = (h: DeliveryHealth) => {
  const map: Record<DeliveryHealth, string> = {
    NO_DELIVERABLES: 'bg-red-100 text-red-800',
    NOTHING_DELIVERED: 'bg-red-100 text-red-800',
    AT_RISK: 'bg-orange-100 text-orange-800',
    ON_TRACK: 'bg-green-100 text-green-800',
  };
  const label: Record<DeliveryHealth, string> = {
    NO_DELIVERABLES: 'No deliverables',
    NOTHING_DELIVERED: 'Nothing delivered',
    AT_RISK: 'At risk',
    ON_TRACK: 'On track',
  };
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${map[h]}`}>{label[h]}</span>;
};

const subscriptionBadge = (flag: SubscriptionFlag, days: number | null) => {
  switch (flag) {
    case 'NONE':
      return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">No subscription</span>;
    case 'EXPIRED':
      return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Expired</span>;
    case 'CANCELLED':
      return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">Cancelled</span>;
    case 'PAUSED':
      return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Paused</span>;
    case 'EXPIRING_SOON':
      return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700">{days != null ? `${days}d left` : 'Expiring soon'}</span>;
    case 'ACTIVE':
    default:
      return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">{days != null ? `${days}d left` : 'Active'}</span>;
  }
};

// Subscription prices are stored in PAISE (SubscriptionsPage.tsx divides by 100 before display).
// Match that convention so units agree across screens, then show in lakhs for compactness.
const fmtMoney = (paise: number) => `₹${(paise / 100 / 100000).toFixed(1)}L`;

// Per-row plan price: show plain rupees with Indian grouping + billing cadence.
// fmtMoney's lakh-compaction makes small plans (e.g. ₹999/mo) read as "₹0.1L" — misleading here.
const fmtPlanPrice = (paise: number, cycle: string) => {
  const rupees = Math.round(paise / 100);
  const cadence = cycle === 'MONTHLY' ? 'month' : cycle === 'QUARTERLY' ? 'quarter' : cycle === 'ANNUAL' ? 'year' : cycle.toLowerCase();
  return `₹${rupees.toLocaleString('en-IN')} / ${cadence}`;
};

const ClientHealthPage: React.FC = () => {
  const navigate = useNavigate();
  const [healthFilter, setHealthFilter] = useState<'ALL' | DeliveryHealth>('ALL');
  // When true, the table is filtered to clients without a subscription (flag === 'NONE').
  const [noSubOnly, setNoSubOnly] = useState(false);
  // Client currently targeted by the Assign Plan modal (null = closed).
  const [assignTarget, setAssignTarget] = useState<{ leadId: string; company: string } | null>(null);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['client-health'],
    queryFn: () => api.getClientHealth(),
    refetchOnWindowFocus: false,
  });

  const clients = data?.clients ?? [];
  const summary = data?.summary;
  const flagged = useMemo(
    () => clients.filter((c) => c.health === 'NO_DELIVERABLES' || c.health === 'NOTHING_DELIVERED'),
    [clients]
  );
  const filtered = useMemo(
    () =>
      clients
        .filter((c) => (healthFilter === 'ALL' ? true : c.health === healthFilter))
        .filter((c) => (noSubOnly ? c.subscriptionFlag === 'NONE' : true)),
    [clients, healthFilter, noSubOnly]
  );

  return (
    <DashboardLayout>
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Client Health</h1>
          <p className="text-sm text-gray-500">Subscriptions, renewals & delivery status — one view</p>
        </div>
        <button onClick={() => refetch()} className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50">
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <SummaryCard title="WON clients" value={summary?.totalWonClients ?? 0} icon={<Users className="h-5 w-5 text-blue-600" />} />
        <SummaryCard
          title="No subscription"
          value={summary?.withoutSubscription ?? 0}
          icon={<Ban className="h-5 w-5 text-gray-500" />}
          active={noSubOnly}
          onClick={() => setNoSubOnly((v) => !v)}
        />
        <SummaryCard title="Active subscriptions" value={summary?.withActiveSubscription ?? 0} icon={<CheckCircle2 className="h-5 w-5 text-green-600" />} />
        <SummaryCard title="Expired subs" value={summary?.expired ?? 0} icon={<Ban className="h-5 w-5 text-red-600" />} danger />
        <SummaryCard title="MRR" value={summary ? fmtMoney(summary.mrr) : '—'} icon={<RefreshCw className="h-5 w-5 text-blue-600" />} />
        <SummaryCard title="ARR" value={summary ? fmtMoney(summary.arr) : '—'} icon={<RefreshCw className="h-5 w-5 text-blue-600" />} />
        <SummaryCard title="Delivery gaps" value={summary?.clientsNoDeliverables ?? 0} icon={<PackageX className="h-5 w-5 text-red-600" />} danger />
        <SummaryCard title="Nothing delivered" value={summary?.clientsNothingDelivered ?? 0} icon={<AlertTriangle className="h-5 w-5 text-red-600" />} danger />
        <SummaryCard title="Renewals due (30d)" value={summary?.renewalsDue30 ?? 0} icon={<Clock className="h-5 w-5 text-orange-600" />} />
      </div>

      {/* Red-flag panel */}
      {flagged.length > 0 && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Delivery not happening ({flagged.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {flagged.map((c) => (
              <button key={c.leadId} onClick={() => navigate(`/clients/${c.leadId}/deliverables`)}
                className="w-full flex items-center justify-between p-3 rounded-lg bg-red-50 hover:bg-red-100 text-left">
                <div>
                  <div className="font-medium text-gray-900">{c.company || c.name}</div>
                  <div className="text-xs text-gray-600">{c.plan?.name ?? 'No plan assigned'} · owes {c.deliverables.owed}, has {c.deliverables.existing}, delivered {c.deliverables.delivered}</div>
                </div>
                {healthBadge(c.health)}
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Filter + table */}
      <div className="flex flex-wrap items-center gap-2">
        {(['ALL', 'NO_DELIVERABLES', 'NOTHING_DELIVERED', 'AT_RISK', 'ON_TRACK'] as const).map((f) => (
          <button key={f} onClick={() => setHealthFilter(f)}
            className={`px-3 py-1.5 text-xs rounded-full border ${healthFilter === f ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {f === 'ALL' ? 'All' : f.replace('_', ' ').toLowerCase()}
          </button>
        ))}
        {noSubOnly && (
          <button
            onClick={() => setNoSubOnly(false)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border border-green-300 bg-green-50 text-green-700 hover:bg-green-100"
          >
            No subscription only
            <span aria-hidden className="font-semibold">×</span>
          </button>
        )}
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Client', 'Plan', 'Renewal', 'Owed / Delivered', 'Delivery', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading && (<tr><td colSpan={7} className="px-6 py-8 text-center text-gray-400">Loading…</td></tr>)}
              {!isLoading && filtered.length === 0 && (<tr><td colSpan={7} className="px-6 py-8 text-center text-gray-400">No clients in scope.</td></tr>)}
              {filtered.map((c: ClientHealthRow) => (
                <tr key={c.leadId} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/clients/${c.leadId}/deliverables`)}>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{c.company || c.name}</div>
                    <div className="text-xs text-gray-500">{c.subscriptionFlag}{c.autoRenew ? ' · auto-renew' : ''}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {c.plan ? (
                      <>
                        {c.plan.name}
                        <div className="text-xs text-gray-400">{fmtPlanPrice(c.plan.price, c.plan.billingCycle)}</div>
                      </>
                    ) : (
                      <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">No plan assigned</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {subscriptionBadge(c.subscriptionFlag, c.daysToRenewal)}
                    <div className="text-xs text-gray-400 mt-1">{c.renewalDate ? new Date(c.renewalDate).toLocaleDateString('en-IN') : '—'}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{c.deliverables.delivered}/{Math.max(c.deliverables.owed, c.deliverables.existing)}<div className="text-xs text-gray-400">{c.deliverables.overdue} overdue · {c.deliverables.notStarted} not started</div></td>
                  <td className="px-6 py-4">{healthBadge(c.health)}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{c.deliverables.inProgress} in progress</td>
                  <td className="px-6 py-4 text-sm" onClick={(e) => e.stopPropagation()}>
                    {c.subscriptionFlag === 'NONE' ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setAssignTarget({ leadId: c.leadId, company: c.company || c.name });
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
                      >
                        <PackageCheck className="h-3.5 w-3.5" /> Assign Plan
                      </button>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <AssignPlanModal
        isOpen={assignTarget !== null}
        leadId={assignTarget?.leadId ?? ''}
        company={assignTarget?.company ?? ''}
        onClose={() => setAssignTarget(null)}
        onSuccess={() => refetch()}
      />
    </div>
    </DashboardLayout>
  );
};

const SummaryCard: React.FC<{
  title: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  danger?: boolean;
  onClick?: () => void;
  active?: boolean;
}> = ({ title, value, icon, danger, onClick, active }) => {
  const interactive = !!onClick;
  return (
    <Card
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick!();
              }
            }
          : undefined
      }
      aria-pressed={interactive ? active : undefined}
      className={[
        danger ? 'border-red-200' : '',
        interactive ? 'cursor-pointer transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400' : '',
        active ? 'ring-2 ring-gray-900 border-gray-900' : '',
      ].join(' ')}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-500">{title}</span>
          {icon}
        </div>
        <div className={`mt-2 text-2xl font-bold ${danger ? 'text-red-700' : 'text-gray-900'}`}>{value}</div>
        {interactive && (
          <div className="mt-1 text-[11px] text-gray-400">{active ? 'Filtering — click to clear' : 'Click to filter'}</div>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientHealthPage;
